import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import { getCalendarParts, formatTime } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const StudentLiveClass = () => {
  const [filter, setFilter] = useState('upcoming');
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subData, coursesData] = await Promise.all([
          api.getMySubscription().catch(() => ({ subscription: null, isActive: false })),
          api.getCourses({ limit: '100' }).catch(() => ({ courses: [] })),
        ]);

        setSubscription(subData.subscription);

        if (!subData.isActive || !subData.subscription) {
          setLoading(false);
          return;
        }

        const allowedIds = getAllowedCourseIds(subData.subscription);
        const courses = coursesData.courses || [];
        const subscribedCourses = allowedIds.length > 0
          ? courses.filter(c => allowedIds.includes(c._id.toString()))
          : courses;

        const classResults = await Promise.all(
          subscribedCourses.map(c =>
            api.getClassesByCourse(c._id)
              .then(d => (d.classes || []).map(cls => ({
                ...cls,
                courseName: c.title,
                courseId: c._id,
                courseThumbnail: c.thumbnail,
              })))
              .catch(() => [])
          )
        );

        const allClasses = classResults
          .flat()
          .filter(cls => cls.status === 'published')
          .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

        setClasses(allClasses);
      } catch (err) {
        console.error('Error fetching classes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getAllowedCourseIds = (sub) => {
    if (!sub?.allowedCourses) return [];
    return sub.allowedCourses.map(c => (c._id || c).toString());
  };

  const now = new Date();
  const upcomingClasses = classes.filter(c => new Date(c.scheduledDate) >= now);
  const pastClasses = classes.filter(c => new Date(c.scheduledDate) < now).reverse();
  const filteredClasses = filter === 'upcoming' ? upcomingClasses : pastClasses;

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-16 text-dark-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-3" />
              Loading your classes...
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-10 text-center">
              <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h2 className="text-xl font-bold text-dark-900 mb-2">No Active Subscription</h2>
              <p className="text-dark-500 mb-6">You need an active subscription to view and attend classes.</p>
              <Link to="/student/dashboard" className="btn-primary inline-block">Go to Dashboard</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      <SEO title="Live Classes" noIndex />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <nav className="flex items-center gap-2 text-sm text-dark-400 mb-3">
              <Link to="/student/dashboard" className="hover:text-primary-500">Dashboard</Link>
              <span>/</span>
              <span className="text-dark-900">Live Classes</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-dark-900 mb-1">Live Classes</h1>
            <p className="text-dark-500">Join live sessions and review past classes.</p>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setFilter('upcoming')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'upcoming' ? 'bg-primary-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              Upcoming ({upcomingClasses.length})
            </button>
            <button
              onClick={() => setFilter('past')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === 'past' ? 'bg-primary-500 text-white' : 'bg-white text-dark-600 hover:bg-dark-100'
              }`}
            >
              Past ({pastClasses.length})
            </button>
          </div>

          {/* Classes List */}
          {filteredClasses.length > 0 ? (
            <div className="space-y-3">
              {filteredClasses.map((cls) => {
                const classDate = new Date(cls.scheduledDate);
                const isPast = classDate < now;
                const isToday = classDate.toDateString() === now.toDateString();
                const teacherName = cls.teacher && typeof cls.teacher === 'object' ? cls.teacher.name : null;
                const parts = getCalendarParts(cls.scheduledDate);

                return (
                  <div key={cls._id} className={`card overflow-hidden ${isToday && !isPast ? 'border-2 border-primary-300 shadow-md' : ''}`}>
                    <div className="flex items-stretch">
                      {/* Date column */}
                      <div className={`flex-shrink-0 w-20 flex flex-col items-center justify-center py-4 ${
                        isPast ? 'bg-dark-100 text-dark-400' : isToday ? 'bg-primary-500 text-white' : 'bg-primary-50 text-primary-700'
                      }`}>
                        <p className="text-xs font-medium uppercase">{parts.month}</p>
                        <p className="text-2xl font-bold leading-none">{parts.day}</p>
                        <p className="text-xs mt-0.5">{parts.weekday}</p>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <h3 className={`font-semibold ${isPast ? 'text-dark-400' : 'text-dark-900'}`}>{cls.title}</h3>
                            {isToday && !isPast && (
                              <span className="flex items-center gap-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                                Today
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-dark-500">
                            {formatTime(cls.scheduledDate)}
                            {cls.duration ? ` · ${cls.duration} min` : ''}
                            {teacherName ? ` · ${teacherName}` : ''}
                          </p>
                          <Link to={`/courses/${cls.courseId}`} className="text-xs text-primary-500 hover:text-primary-600 mt-1 inline-block">
                            {cls.courseName}
                          </Link>
                        </div>

                        {/* Action */}
                        {!isPast ? (
                          cls.meetingLink ? (
                            <a href={cls.meetingLink} target="_blank" rel="noopener noreferrer"
                              className="flex-shrink-0 btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Join Class
                            </a>
                          ) : (
                            <span className="flex-shrink-0 text-sm text-dark-400 px-4 py-2">Link coming soon</span>
                          )
                        ) : (
                          <span className="flex-shrink-0 text-xs text-dark-400 bg-dark-50 px-3 py-1.5 rounded">Completed</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <svg className="w-16 h-16 text-dark-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-dark-900 mb-2">
                {filter === 'upcoming' ? 'No Upcoming Classes' : 'No Past Classes'}
              </h3>
              <p className="text-dark-500">
                {filter === 'upcoming'
                  ? 'There are no upcoming classes scheduled for your courses.'
                  : 'No past classes to show yet.'}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentLiveClass;
